import React, { useEffect, useRef, useState } from 'react'
import { FloatingBubble } from 'antd-mobile'
import { history } from 'umi'

import aiChatbotEntry from '../../assets/image/ai_chatbot_entry.png'
import goTo from '../../libs/functions/goTo'
import { getStorage } from '../../libs/functions/onStorage'
import { AiChatEntryResponse, getAiChatEntry } from '../../services'

const H5_TOKEN_STORAGE_KEY = 'h5Token'
const CHATBOT_ROUTE = '/chatbot/room'
const LOGIN_ROUTE = '/login'
const UNAUTHENTICATED_SYNC_INTERVAL = 2000
const AUTHENTICATED_SYNC_INTERVAL = 60 * 1000

type EntryStatus = 'checking' | 'enabled' | 'stopped'

const getH5Token = () => getStorage(H5_TOKEN_STORAGE_KEY) as string | null

const hasH5Token = () => Boolean(getH5Token())

const isChatbotRoute = () => {
  const pathname = history.location.pathname
  return pathname === CHATBOT_ROUTE || pathname.startsWith(`${CHATBOT_ROUTE}/`)
}

const isLoginRoute = () => {
  const pathname = history.location.pathname
  return pathname === LOGIN_ROUTE || pathname.startsWith(`${LOGIN_ROUTE}/`)
}

const shouldShowBubble = () =>
  hasH5Token() && !isChatbotRoute() && !isLoginRoute()

const bubbleStyle = {
  '--edge-distance': '15px',
  '--initial-position-bottom': (screen.height / 3.5 || 22).toFixed(0) + 'px',
  '--initial-position-right': '15px',
  '--z-index': '1000',
  '--size': '56px',
  '--background': 'transparent',
}

const imageStyle = {
  display: 'block',
  width: '100%',
  height: '100%',
  objectFit: 'contain' as const,
}

export default function AiChatbotBubble() {
  const [entryConfig, setEntryConfig] = useState<AiChatEntryResponse | null>(
    null,
  )
  const [visible, setVisible] = useState(false)
  const [entryStatus, setEntryStatus] = useState<EntryStatus>('checking')
  // 记录已为哪个 token 拉过入口配置：同一个 token 只拉一次，换 token（重新登录）后要再拉一次
  const requestedTokenRef = useRef<string | null>(null)

  useEffect(() => {
    if (entryStatus === 'stopped') {
      setVisible(false)
      return
    }

    let isUnmounted = false
    let timer: number | undefined

    const getSyncInterval = () =>
      entryStatus === 'enabled' && hasH5Token()
        ? AUTHENTICATED_SYNC_INTERVAL
        : UNAUTHENTICATED_SYNC_INTERVAL

    const loadEntryConfig = () => {
      const token = getH5Token()
      // 请求与悬浮球显隐共用同一套规则：无 token / 登录页 / 聊天页都不请求
      if (!token || !shouldShowBubble()) return
      // 同一个 token 只请求一次；token 变了（重新登录）要重新请求
      if (requestedTokenRef.current === token) return

      requestedTokenRef.current = token
      getAiChatEntry()
        .then((entryConfig) => {
          if (isUnmounted) return
          if (entryConfig?.enabled === true) {
            setEntryConfig(entryConfig)
            setVisible(shouldShowBubble())
            setEntryStatus('enabled')
            return
          }
          setEntryStatus('stopped')
        })
        .catch(() => {
          if (!isUnmounted) {
            setEntryStatus('stopped')
          }
        })
    }

    const syncVisible = () => {
      if (!hasH5Token()) {
        setVisible(false)
        return
      }
      // 每次同步都尝试补拉入口配置：登录页/聊天页被挡下，离开后或换 token 后能补上
      loadEntryConfig()
      if (entryStatus === 'enabled') {
        setVisible(shouldShowBubble())
      }
    }
    const scheduleSync = () => {
      if (timer !== undefined) {
        window.clearTimeout(timer)
      }
      timer = window.setTimeout(() => {
        syncVisible()
        scheduleSync()
      }, getSyncInterval())
    }
    const syncVisibleAndSchedule = () => {
      syncVisible()
      scheduleSync()
    }
    const handleStorage = (event: StorageEvent) => {
      if (!event.key || event.key === H5_TOKEN_STORAGE_KEY) {
        syncVisibleAndSchedule()
      }
    }

    const unlisten = history.listen(syncVisibleAndSchedule)
    syncVisible()
    scheduleSync()
    window.addEventListener('storage', handleStorage)

    return () => {
      unlisten()
      if (timer !== undefined) {
        window.clearTimeout(timer)
      }
      window.removeEventListener('storage', handleStorage)
    }
  }, [entryStatus])

  if (entryStatus !== 'enabled' || !visible) return null

  return (
    <FloatingBubble
      axis="xy"
      magnetic="x"
      style={bubbleStyle}
      aria-label="AI Chatbot"
      onClick={() => goTo('/chatbot/room')}
    >
      <img
        src={entryConfig?.iconUrl || aiChatbotEntry}
        alt=""
        aria-hidden="true"
        style={imageStyle}
      />
    </FloatingBubble>
  )
}
